/**
 * fieldRegistry.js — maps a config field's `control` name to the primitive component that
 * renders it
 */

import { InputField } from "../../primary/InputField/InputField";
import { SelectField } from "../../primary/SelectField/SelectField";
import { ChipGroup } from "../../secondary/ChipGroup/ChipGroup";
import { MultiSelect } from "../../primary/MultiSelect/multiSelect";
import { buildValidationRule } from "./validation";

const registry = {};

export const registerControl = (name, Component, mapProps) => {
  registry[name] = { Component, mapProps };
};

export const getControl = (name) => registry[name];

registerControl("text", InputField, ({ field, form, errors, onFieldChange, register, customValidators, disabled }) => {
  const rule = buildValidationRule(field.validation, customValidators);
  return {
    id: field.key,
    name: field.key,
    type: field.type || "text",
    defaultValue: form[field.key],
    suffix: field.suffix,
    placeholder: field.placeholder,
    disabled: disabled || field.disabled,
    required: rule ? false : !!field.required,
    dataTestId: field.dataTestId || field.key,
    error: errors?.[field.key]?.message,
    inputRef: register ? register(rule || {}) : undefined,
    onChange: (e) => onFieldChange(field.key, e.target.value),
  };
});

registerControl("select", SelectField, ({ field, form, errors, onFieldChange, register, customValidators, disabled }) => {
  const rule = buildValidationRule(field.validation, customValidators);
  return {
    id: field.key,
    name: field.key,
    options: field.options || [],
    defaultValue: form[field.key],
    placeholder: field.placeholder,
    disabled: disabled || field.disabled,
    required: field.required,
    dataTestId: field.dataTestId || `select_${field.key}`,
    error: errors?.[field.key]?.message,
    inputRef: register ? register(rule || {}) : undefined,
    onChange: (e) => onFieldChange(field.key, e.target.value),
  };
});

registerControl("chip", ChipGroup, ({ field, form, errors, onFieldChange, disabled }) => ({
  name: field.key,
  options: field.options || [],
  selected: form[field.key],
  multiple: !!field.multiple,
  disabled: disabled || field.disabled,
  errors,
  onToggle: (_key, nextValue) => onFieldChange(field.key, nextValue),
}));

registerControl("multiselect", MultiSelect, ({ field, form, onMultiChange, submitErrors, disabled }) => ({
  id: field.key,
  name: field.key,
  optionValue: field.options || [],
  defaultValue: form[field.key] || [],
  disabled: disabled || field.disabled,
  required: field.required,
  showCheckbox: field.showCheckbox ?? true,
  displayValue: field.displayValue || "value",
  showSelectAll: !!field.showSelectAll,
  onSelect: (opts) => onMultiChange(field.key, opts),
  onRemove: (opts) => onMultiChange(field.key, opts),
  customErrors: field.errorFlagKey ? !!submitErrors?.[field.errorFlagKey] : false,
  customErrorsMsg: field.errorMessage,
}));
